import { Injectable } from '@angular/core';
import { QuestionnaireTypes, QuestionTypes } from '@auto-editor/shared/enums';
import { ActionModel, NodeAnalysisModel, NodeModel, OptionModel, QuestionnaireListDto, QuestionnaireModel } from '@auto-editor/shared/models';
import * as _ from 'lodash-es';
import { BehaviorSubject, Subject } from 'rxjs';
import { v4 as uuidV4 } from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class UtilsService {
  hideSave: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  systemLanguage: BehaviorSubject<string> = new BehaviorSubject<string>('es');
  languageToTranslate: string;
  dataQuestionnaireGET: QuestionnaireModel;
  languageSelectedFakePreview: string;
  questionnaireChanged: Subject<void> = new Subject<void>();
  closePanels: Subject<void> = new Subject<void>();
  editingQuestion: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  logicChanged: Subject<void> = new Subject<void>();

  triggerCreateView() {
    this.logicChanged.next();
  }

  private _questionSelected: NodeModel;
  get questionSelected(): NodeModel {
    return this._questionSelected;
  }
  set questionSelected(value: NodeModel) {
    this._questionSelected = value;
  }
  
  private _questionnaire: QuestionnaireListDto;
  get questionnaire(): QuestionnaireListDto {
    return this._questionnaire;
  }
  set questionnaire(value: QuestionnaireListDto) {
    this._questionnaire = value;
  }
  
  _languageDefault: string;
  get languageDefault(): string {
    return this._languageDefault;
  }
  set languageDefault(value: string) {
    this._languageDefault = value;
  }
  
  private _dataQuestionnaire: any;
  get dataQuestionnaire(): QuestionnaireModel {
    return this._dataQuestionnaire;
  }
  set dataQuestionnaire(value: any) {
    this._dataQuestionnaire = value;
  }
  
  private _animationsQuestionnaire: any;
  get animationsQuestionnaire(): any {
    return this._animationsQuestionnaire;
  }
  set animationsQuestionnaire(value: any) {
    this._animationsQuestionnaire = value;
  }

  readonly DEFAULT_TEXT: string = null;

  init() {
    this.editingQuestion = new BehaviorSubject<string>(null);
    this.questionnaireChanged = new Subject<void>();
    this.closePanels = new Subject<void>();
    this.logicChanged = new Subject<void>();
  }

  clearServiceData() {
    this.dataQuestionnaire = undefined;
    this.animationsQuestionnaire = undefined;
    this.languageSelectedFakePreview = undefined;
    this.questionSelected = undefined;
    this.editingQuestion.complete();
    this.questionnaireChanged.complete();
    this.closePanels.complete();
    this.logicChanged.complete();
  }

  getUUID() {
    return uuidV4();
  }

  isTAQT(): boolean {
    return this.dataQuestionnaire?.type === QuestionnaireTypes.TAQT;
  }

  /** Busca en el cuestionario el nodo con ese id (también dentro de grupos) */
  findQuestionNode(id: string, nodes: NodeModel[] = this.dataQuestionnaire?.nodes): NodeModel | undefined {
    for (const node of nodes ?? []) {
      if (node.id === id) return node;
      const found = node.nodes ? this.findQuestionNode(id, node.nodes) : undefined;
      if (found) return found;
    }
    return undefined;
  }

  hasButtons() {
    return this.hasButtonsRecursive(this.dataQuestionnaire.nodes);
  }

  hasButtonsRecursive(nodes: NodeModel[]) {
    for (const node of nodes) {
      if (node._metadata.alias === QuestionTypes.QButtons) return true;
      if (node.type === 'GR' && this.hasButtonsRecursive(node.nodes)) return true;
    }
    return false;
  }

  hasQuestionType(type: string) {
    return this.hasQuestionTypeRecursive(this.dataQuestionnaire.nodes, type);
  }

  hasQuestionTypeRecursive(nodes: NodeModel[], type: string) {
    for (const node of nodes) {
      if (node._metadata.alias === type) return true;
      if (node.type === 'GR' && this.hasQuestionTypeRecursive(node.nodes, type)) return true;
    }
    return false;
  }

  hasNPS() {
    return this.hasNPSRecursive(this.dataQuestionnaire.nodes);
  }

  hasNPSRecursive(nodes: NodeModel[]) {
    for (const node of nodes) {
      if (node._metadata.alias === QuestionTypes.QNPS) return true;
      if (node.type === 'GR' && this.hasNPSRecursive(node.nodes)) return true;
    }
    return false;
  }

  countSkipLogics() {
    let n = 0;
    return this.countSkipLogicsRecursive(this.dataQuestionnaire.nodes, n);
  }

  countSkipLogicsRecursive(nodes: NodeModel[], n: number) {
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].defaultJumpTo != null) n++;
      if (nodes[i].input && nodes[i].input.options) {
        nodes[i].input.options.forEach((option) => {
          if (option.jumpTo != null) n++;
        });
      }
      if (nodes[i].nodes) this.countSkipLogicsRecursive(nodes[i].nodes, n);
    }
    return n;
  }

  eliminateSkipLogics() {
    this.eliminateSkipLogicsRecursive(this.dataQuestionnaire.nodes);
  }

  eliminateSkipLogicsRecursive(nodes: NodeModel[]) {
    for (let i = 0; i < nodes.length; i++) {
      nodes[i].defaultJumpTo = null;
      if (nodes[i].input && nodes[i].input.options) {
        nodes[i].input.options.forEach((option) => (option.jumpTo = null));
      }
      if (nodes[i].nodes) this.eliminateSkipLogicsRecursive(nodes[i].nodes);
    }
  }

  countQuestions() {
    let n = 0;
    n = this.countQuestionsRecursive(this.dataQuestionnaire.nodes, n);
    return n;
  }

  countQuestionsRecursive(nodes: NodeModel[], n: number) {
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].type === 'Q') n++;
      if (nodes[i].nodes && nodes[i].nodes.length !== 0) {
        n = this.countQuestionsRecursive(nodes[i].nodes, n);
      }
    }
    return n;
  }

  updateAnimationsJSON() {
    const obj = [];
    this.setAnimationRecursive(this.dataQuestionnaire.nodes, obj);
    this.animationsQuestionnaire = obj;
  }

  setAnimationRecursive(nodes: NodeModel[], obj: any) {
    nodes.forEach((node) => {
      obj.push({ entrance: _.cloneDeep(node.entrance) });
    });
  }

  moveAnimationsToJSON() {
    this.dataQuestionnaire.nodes.forEach((node: NodeModel, index: number) => {
      node.entrance = _.cloneDeep(this.animationsQuestionnaire[index].entrance);
    });
  }

  setTranslations(languageResult: any) {
    this.deleteTranslations(languageResult.newLanguages);
    this.addTranslations(languageResult.newLanguages);
    this.replaceMainLanguage(languageResult.defaultLanguage);
    this.setNewArrayLanguages(languageResult.newLanguages);
  }

  deleteTranslations(newArrLanguages: string[]) {
    this.dataQuestionnaire.langs.forEach((lang) => {
      const indexLanguage = newArrLanguages.findIndex((l) => l === lang);
      if (indexLanguage === -1) {
        if (
          this.dataQuestionnaire.template.values &&
          this.dataQuestionnaire.template.values.images &&
          this.dataQuestionnaire.template.values.images.logo
        )
          delete this.dataQuestionnaire.template.values.images.logo[lang];
        if (
          this.dataQuestionnaire.template.values &&
          this.dataQuestionnaire.template.values.images &&
          this.dataQuestionnaire.template.values.images.customerlogo
        )
          delete this.dataQuestionnaire.template.values.images.customerlogo[lang];
        if (
          this.dataQuestionnaire.template.values &&
          this.dataQuestionnaire.template.values.images &&
          this.dataQuestionnaire.template.values.images.background
        )
          delete this.dataQuestionnaire.template.values.images.background[lang];
        if (this.hasEndButton()) {
          const endButton = this.dataQuestionnaire.actions.find((action) => action.controller === 'EndButton');
          delete endButton.template.values.title[lang];
        }
        this.deleteTranslationsRecursive(lang, this.dataQuestionnaire.nodes);
      }
    });
  }

  hasEndButton() {
    return this.dataQuestionnaire.actions && this.dataQuestionnaire.actions.findIndex((action) => action.controller === 'EndButton') !== -1;
  }

  deleteTranslationsRecursive(lang: string, nodes: NodeModel[]) {
    if (!nodes || nodes.length === 0) return;
    for (const node of nodes) {
      this.deleteTranslationsActions(lang, node.actions);
      const values = node.template.values;
      if (values?.title) delete values.title[lang];
      if (values?.description) delete values.description[lang];
      if (values?.qImage) delete values.qImage[lang];
      if (node.input) {
        if (node.input.template) {
          const inputValues = node.input.template.values;
          if (inputValues?.agree) delete inputValues.agree[lang]; // Puede que estos deban cambiar porque son de QLegalAgreement 1 sola checkbox
          if (inputValues?.alert) delete inputValues.alert[lang]; // idem
        }
        if (node.input.options) {
          this.deleteTranslationsOptions(lang, node.input.options);
        }
        if (node.input.hints) {
          node.input.hints.forEach((hint) => delete hint.title[lang]);
        }
      }
      if (node.nodes) this.deleteTranslationsRecursive(lang, node.nodes);
    }
  }

  deleteTranslationsOptions(lang: string, options: OptionModel[]) {
    if (!options || options.length === 0) return;
    options.forEach((option) => {
      if (option.title) delete option.title[lang];
      if (option.img) delete option.img[lang];
    });
  }

  deleteTranslationsActions(lang: string, actions: ActionModel[]) {
    if (!actions || actions.length === 0) return;
    actions.forEach((action) => {
      if (action.template?.values?.title) delete action.template.values.title[lang];
    });
  }

  addTranslations(newArrLanguages: string[]) {
    newArrLanguages.forEach((lang, index) => {
      const indexLanguage = this.dataQuestionnaire.langs.findIndex((l) => l === lang);
      if (indexLanguage === -1) {
        this.addTranslationsSurvey(lang);
      }
    });
  }

  addTranslationsSurvey(lang) {
    if (this.dataQuestionnaire.template.values && this.dataQuestionnaire.template.values.images && this.dataQuestionnaire.template.values.images.logo)
      this.dataQuestionnaire.template.values.images.logo[lang] = this.dataQuestionnaire.template.values.images.logo[this.languageDefault];
    if (
      this.dataQuestionnaire.template.values &&
      this.dataQuestionnaire.template.values.images &&
      this.dataQuestionnaire.template.values.images.customerlogo
    )
      this.dataQuestionnaire.template.values.images.customerlogo[lang] =
        this.dataQuestionnaire.template.values.images.customerlogo[this.languageDefault];
    if (
      this.dataQuestionnaire.template.values &&
      this.dataQuestionnaire.template.values.images &&
      this.dataQuestionnaire.template.values.images.background
    )
      this.dataQuestionnaire.template.values.images.background[lang] = this.dataQuestionnaire.template.values.images.background[this.languageDefault];
    if (this.hasEndButton()) {
      const endButton = this.dataQuestionnaire.actions.find((action) => action.controller === 'EndButton');
      endButton.template.values.title[lang] = endButton.template.values.title[this.languageDefault];
    }
    this.addTranslationsRecursive(lang, this.dataQuestionnaire.nodes);
  }

  addTranslationsRecursive(lang: string, nodes: NodeModel[]) {
    if (!nodes || nodes.length === 0) return;
    for (const node of nodes) {
      this.addTranslationsActions(lang, node.actions);
      const values = node.template.values;
      if (values?.title && values.title[lang] == null) values.title[lang] = this.DEFAULT_TEXT;
      if (values?.description && values.description[this.languageDefault] && values.title[lang] == null) values.description[lang] = this.DEFAULT_TEXT;
      if (values?.qImage) values.qImage[lang] = values.qImage[this.languageDefault];
      if (node.input) {
        if (node.input.template) {
          const inputValues = node.input.template.values;
          if (inputValues?.alert && inputValues.alert[lang] == null) inputValues.alert[lang] = this.DEFAULT_TEXT;
          if (inputValues?.agree && inputValues.agree[lang] == null) inputValues.agree[lang] = this.DEFAULT_TEXT;
        }
        if (node.input.options) {
          this.addTranslationsOptions(lang, node.input.options);
        }
        if (node.input.hints) {
          node.input.hints.forEach((hint) => {
            if (hint.title[lang] == null) hint.title[lang] = this.DEFAULT_TEXT;
          });
        }
      }
      if (node.nodes) this.addTranslationsRecursive(lang, node.nodes);
    }
  }

  addTranslationsOptions(lang: string, options: OptionModel[]) {
    if (!options || options.length === 0) return;
    options.forEach((option) => {
      if (option.title) {
        if (option.title[lang] == null) {
          if (option.id === option.responseValue) option.title[lang] = this.DEFAULT_TEXT;
          else option.title[lang] = option.title[this.languageDefault];
        }
      }
      if (option.img) option.img[lang] = option.img[this.languageDefault];
    });
  }

  addTranslationsActions(lang: string, actions: ActionModel[]) {
    if (!actions || actions.length === 0) return;
    actions.forEach((action) => {
      if (action.template?.values?.title && action.template.values.title[lang] == null) action.template.values.title[lang] = this.DEFAULT_TEXT;
    });
  }

  replaceMainLanguage(defaultLanguage: string) {
    this.languageDefault = defaultLanguage;
    this.dataQuestionnaire.defaultLang = defaultLanguage;
  }

  setNewArrayLanguages(newArrLanguages: string[]) {
    this.dataQuestionnaire.langs = newArrLanguages;
  }

  createAllLanguages(text?: string): any {
    const obj = {};
    this.dataQuestionnaire.langs.forEach((lang) => {
      if (lang === this.dataQuestionnaire.defaultLang)
        if (text) obj[lang] = text;
        else obj[lang] = this.DEFAULT_TEXT;
      else obj[lang] = this.DEFAULT_TEXT;
    });
    return obj;
  }

  createAllLanguagesSame(text): any {
    const obj = {};
    this.dataQuestionnaire.langs.forEach((lang) => {
      obj[lang] = text;
    });
    return obj;
  }

  orderNextsAndPrevious(node: NodeModel) {
    if (node.actions) {
      const nextIndex = node.actions.findIndex((action) => action.controller === 'NavNext');
      if (nextIndex !== -1) {
        const next = node.actions.splice(nextIndex, 1)[0];
        node.actions.push(next);
      }
      const prevIndex = node.actions.findIndex((action) => action.controller === 'NavPrev');
      if (prevIndex !== -1) {
        const prev = node.actions.splice(prevIndex, 1)[0];
        node.actions.unshift(prev);
      }
    }
  }

  eliminateNextsAndPrevious() {
    let prevIndex = this.dataQuestionnaire.nodes[0].actions.findIndex((action) => action.controller === 'NavPrev');
    if (prevIndex !== -1) {
      this.dataQuestionnaire.nodes[0].actions.splice(prevIndex, 1);
    }
    let nextIndex = this.dataQuestionnaire.nodes[this.dataQuestionnaire.nodes.length - 1].actions.findIndex(
      (action) => action.controller === 'NavNext'
    );
    if (nextIndex !== -1) {
      this.dataQuestionnaire.nodes[this.dataQuestionnaire.nodes.length - 1].actions.splice(nextIndex, 1);
    }
  }

  duplicateQuestionRecursive(node: NodeModel, nodeAnalysis?: NodeAnalysisModel) {
    node.id = this.getUUID();
    if (nodeAnalysis) nodeAnalysis.questionId = node.id;
    if (node.input?.options) {
      node.input.options.forEach((option) => {
        const newId = this.getUUID();
        if (option.type !== 'NSNC') {
          option.id = newId;
          if (node._metadata.alias === QuestionTypes.QOptions || node._metadata.alias === QuestionTypes.QOptionsMultiple) {
            option.responseValue = newId;
          }
        }
      });
    }
    if (node.nodes) {
      node.nodes.forEach((subNode, index) => {
        this.duplicateQuestionRecursive(subNode, nodeAnalysis ? nodeAnalysis.nodes[index] : undefined);
      });
    }
  }

  isOldVersion() {
    return this.questionnaire.newVersion === false;
  }
}
